import { apiErrorText } from "../../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../../i18n/portal.js";
/**
 * `data-screen-label="Agent"`, tab `review` — Gaps and Flagged (P11-03).
 *
 * TWO PANES OVER TWO TABLES, AND THEY ARE NOT THE SAME KIND OF THING.
 *
 *   **Gaps** (`gap`) are questions callers asked that the documents do not answer, already
 *   clustered and counted by the runtime. Writing an answer here **queues it into the next
 *   version and publishes nothing**: the route fills `gap.answer_text` and leaves
 *   `answered_into_agent_version_id` null until "Publish answers as a new version" folds it
 *   into one. The screen says exactly that, because "queued" and "live" are the difference
 *   between the agent still guessing and the agent knowing.
 *
 *   **Flagged** (`conversation_flag`) is **one table viewed by two populations**. A member
 *   flags a conversation from History; an operator flags one from the staff console; the
 *   runtime flags one automatically; a carrier reports one. All four land in the same table
 *   and `raised_by_kind` is what tells them apart, with `severity` saying how loudly. This
 *   screen shows all four and labels who raised each — hiding the operator-raised ones would
 *   leave a customer looking at a list that does not match what WeTalk can see.
 *
 * WHO RAISED IT IS A POPULATION, NOT A NAME. `person` lives behind the identity database role
 * and the tenant role has no grant on it, so no route can turn `raised_by_person_id` into
 * "Marco" and this screen does not invent one. "Someone on your team" is the true thing the
 * schema supports.
 *
 * WHAT THE DESIGN ASKS FOR THAT IS NOT DRAWN, AND WHY.
 *
 *   · **"Listen" plays nothing here.** Playing a recording is an audited act with its own
 *     route (design §8.8) and that route does not exist yet, so Listen opens the conversation
 *     in History, where the recording reference and the reason it cannot yet be played are
 *     already shown honestly. A player that could not play would be worse.
 *   · **"Publish answers as a new version"** (built 2026-09-30, the product owner: "I saved an
 *     answer but I cannot publish it") folds every queued answer into a new version and
 *     publishes it at once — an answer is configuration, so there is nothing to build. It is
 *     enabled while at least one answer is queued, and quotes the live sequence as a publish does.
 *   · **"Open the call"** (review #93) opens, in History, the latest conversation in which the
 *     agent recorded the question as one it could not answer — the API links each gap to it now.
 *     A gap no recorded question matches has no call to open, and the button says so.
 *
 * AMENDED 2026-10-09 (plan-015 DRF, gap G9, ADR 0043 — owner answer 3): "Publish answers as a new version" is
 * the design's **"Save as a draft"** ("Save 3 answers as a draft"). It makes a draft — a built version that
 * answers no calls until someone who may publish today publishes it from Versions — carrying every answer not
 * yet live. A gap whose answer sits in a draft reads **"In a draft"** (the draft's label, from the version list:
 * a version never published) until that draft is published. The live version keeps answering meanwhile.
 *
 * WHERE THE GAPS COME FROM (review #93). The runtime records every question it could not ground
 * as an `unanswered_question` outcome; the API turns those into gaps, grouped and counted per
 * conversation, before it answers this list. The headline is the caller's own wording where the
 * gap is exactly that question, because the grouped form is lower-cased.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Field, Icon, Tag, Textarea } from "@wetalk/ui";
import { useState, type ReactNode } from "react";

import { GLYPH } from "../../../shell/glyph.js";
import { useGateway } from "../../../api/provider.js";
import { QUERY_KEY, agentVersionListQuery, conversationFlagQuery, gapQuery } from "../../../api/query.js";
import { formatDuration } from "../../../money.js";
import type { AgentVersionSummaryView, ConversationFlagView, GapView } from "../../../api/gateway.js";

/** Which pane the URL is on. */
export const CONCERN_PANE = ["gaps", "flagged"] as const;
export type ConcernPane = (typeof CONCERN_PANE)[number];

export function isConcernPane(value: string): value is ConcernPane {
  return (CONCERN_PANE as readonly string[]).includes(value);
}

export interface GapAndConversationFlagTabProps {
  readonly voiceAgentId: string;
  /** The live version's sequence, quoted by the publish; `null` while nothing is live. */
  readonly liveVersionSeq: number | null;
  readonly pane: ConcernPane;
  readonly onPane: (value: ConcernPane) => void;
  /** Opens the conversation in History, which is where its evidence already lives. */
  readonly onOpenConversation: (conversationId: string) => void;
  /**
   * plan-015 DRF: opens a version on the Versions tab (the design jumps to the new draft after "Save as a draft").
   * Optional until the agent screen passes it; without it the note names the draft and where to publish it.
   */
  readonly onOpenAgentVersion?: (agentVersionId: string) => void;
}

/**
 * The labels of the versions that were never published — a gap answered into one of them is "In a draft"
 * (plan-015 DRF). A label is unique per VoiceAgent.
 */
export function draftLabelSetOf(agentVersion: readonly AgentVersionSummaryView[] | undefined): ReadonlySet<string> {
  return new Set((agentVersion ?? []).filter((one) => !one.isLive && one.publishedAtText === null).map((one) => one.label));
}

/** "Save 3 answers as a draft", or the design's bare "Save as a draft" while nothing is queued. */
export function saveAsDraftLabelOf(queuedGapCount: number): string {
  if (queuedGapCount === 0) return portalText("operations", "save_as_a_draft_459bd1e5");
  return portalText("operations", "save_value_value_as_a_draft_e0074bcc", { v0: String(String(queuedGapCount)), v1: String(queuedGapCount === 1 ? portalText("operations", "answer_25dc282b") : portalText("operations", "answers_e3d9bca2")) });
}

export function GapAndConversationFlagTab(props: GapAndConversationFlagTabProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const gap = useQuery(gapQuery(gateway, props.voiceAgentId));
  const conversationFlag = useQuery(conversationFlagQuery(gateway, props.voiceAgentId));
  const agentVersion = useQuery(agentVersionListQuery(gateway, props.voiceAgentId));
  const draftLabel = draftLabelSetOf(agentVersion.data);

  const queryClient = useQueryClient();
  const queuedGapCount = (gap.data ?? []).filter((one) => one.state === "queued").length;
  const [savedDraft, setSavedDraft] = useState<AgentVersionSummaryView | null>(null);
  const publish = useMutation({
    mutationFn: (expectedLiveVersionSeq: number) => gateway.publishAnswers(props.voiceAgentId, expectedLiveVersionSeq),
    async onSuccess() {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.gap }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent }),
        queryClient.invalidateQueries({ queryKey: QUERY_KEY.agentVersion }),
      ]);
      // The draft just made is the newest version never published.
      const listed = await queryClient.fetchQuery(agentVersionListQuery(gateway, props.voiceAgentId));
      const made = listed
        .filter((one) => !one.isLive && one.state === "ready" && one.publishedAtText === null)
        .sort((left, right) => right.ordinal - left.ordinal)[0];
      setSavedDraft(made ?? null);
      if (made !== undefined && props.onOpenAgentVersion !== undefined) props.onOpenAgentVersion(made.agentVersionId);
    },
    // A 409 means somebody published meanwhile: refetch the sequence, never retry the refused one.
    async onError() {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.voiceAgent });
    },
  });

  return (
    <>
      <div className="c-va-titlerow">
        <div>
          <p className="c-va-subtitle">{portalText("operations", "what_the_agent_could_not_handle_ec7d88a5")}</p>
        </div>
        <Button
          variant="primary"
          disabled={queuedGapCount === 0 || props.liveVersionSeq === null || publish.isPending}
          title={queuedGapCount === 0 ? portalText("operations", "write_an_answer_on_a_question_first_f1d98be1") : undefined}
          onClick={() => {
            if (props.liveVersionSeq !== null) publish.mutate(props.liveVersionSeq);
          }}
        >
          <Icon glyph={GLYPH.gitBranch} size="md" />
          <span>{publish.isPending ? portalText("operations", "saving_56a2285c") : saveAsDraftLabelOf(queuedGapCount)}</span>
        </Button>
      </div>
      {publish.error !== null ? (
        <p className="c-note c-note-critical">
          {portalText("operations", "no_draft_was_saved_7470bf0d")}{apiErrorText(publish.error, "")}
        </p>
      ) : null}
      {publish.data !== undefined && publish.error === null ? (
        <p className="c-note">
          {portalText("operations", "saved_as_0619f994")}{savedDraft === null ? portalText("operations", "a_draft_4de5a9e8") : portalText("operations", "draft_value_48b3d485", { v0: String(savedDraft.label) })}{portalText("operations", "nothing_changes_on_live_calls_until_you_publish_it_from_ve_0d10473f")}{" "}
          {publish.data.publishedGapCount === 1 ? portalText("operations", "this_question_b4fd1749") : portalText("operations", "these_questions_348d14ae")}.
        </p>
      ) : null}

      <div className="c-va-seg c-va-seg-spaced" role="tablist" aria-label={portalText("operations", "review_panes_08647845")}>
        <button
          type="button"
          role="tab"
          className="c-va-seg-option"
          aria-selected={props.pane === "gaps"}
          onClick={() => {
            props.onPane("gaps");
          }}
        >
          {portalText("operations", "unanswered_questions_e0e9cd50")}<span className="c-va-seg-badge">{String(gap.data?.length ?? 0)}</span>
        </button>
        <button
          type="button"
          role="tab"
          className="c-va-seg-option"
          aria-selected={props.pane === "flagged"}
          onClick={() => {
            props.onPane("flagged");
          }}
        >
          {portalText("operations", "flagged_calls_c6bc02a0")}<span className="c-va-seg-badge">{String(conversationFlag.data?.length ?? 0)}</span>
        </button>
      </div>

      {props.pane === "gaps" ? (
        <GapPane
          voiceAgentId={props.voiceAgentId}
          gap={gap.data}
          draftLabel={draftLabel}
          onOpenConversation={props.onOpenConversation}
        />
      ) : (
        <ConversationFlagPane
          voiceAgentId={props.voiceAgentId}
          row={conversationFlag.data}
          onOpenConversation={props.onOpenConversation}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Gaps
// ---------------------------------------------------------------------------------------

/**
 * The words a gap is headed with: the caller's own wording when the grouped question is just that
 * wording lower-cased (a recorded question), the grouped form otherwise.
 */
export function gapHeadlineOf(one: Pick<GapView, "question" | "representativeQuote">): {
  readonly headline: string;
  readonly quote: string | null;
} {
  const quote = one.representativeQuote;
  if (quote === null) return { headline: one.question, quote: null };
  const same =
    quote.toLocaleLowerCase("el").replace(/\s+/g, " ").replace(/[\s.,;:!?…·;]+$/u, "").trim() ===
    one.question;
  return same ? { headline: quote, quote: null } : { headline: one.question, quote };
}

function GapPane({
  voiceAgentId,
  gap: gapWithFolded,
  draftLabel,
  onOpenConversation,
}: {
  readonly voiceAgentId: string;
  readonly gap: readonly GapView[] | undefined;
  /** The labels of the versions never published (plan-015 DRF). */
  readonly draftLabel: ReadonlySet<string>;
  readonly onOpenConversation: (conversationId: string) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const [answering, setAnswering] = useState<string | null>(null);
  const [draft, setDraft] = useState("");

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.gap });
    // The Analytics tab ranks the same rows, so it goes stale the moment one is answered.
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY.analytics });
  };

  const answer = useMutation({
    mutationFn: (intent: { readonly gapId: string; readonly text: string }) =>
      gateway.answerGap(voiceAgentId, intent.gapId, intent.text),
    async onSuccess() {
      setAnswering(null);
      setDraft("");
      await invalidate();
    },
  });

  const dismiss = useMutation({
    mutationFn: (gapId: string) => gateway.dismissGap(voiceAgentId, gapId),
    onSuccess: invalidate,
  });

  if (gapWithFolded === undefined) return <p className="c-note">{portalText("operations", "reading_the_gaps_a4e77d59")}</p>;
  // A folded gap stays on the list only while its version is a draft (plan-015 G9).
  const gap = gapWithFolded.filter(
    (one) => one.state !== "folded" || (one.answeredIntoLabel !== null && draftLabel.has(one.answeredIntoLabel)),
  );
  if (gap.length === 0) {
    return (
      <p className="c-note">
        {portalText("operations", "nothing_unanswered_is_waiting_every_question_callers_asked_1543865b")}</p>
    );
  }

  return (
    <>
      <p className="c-va-dek">
        {portalText("operations", "questions_callers_asked_that_the_documents_do_not_answer_g_4f1211ad")}</p>

      <div className="c-va-gaps">
        {gap.map((one) => {
          const { headline, quote } = gapHeadlineOf(one);
          return (
          <div key={one.gapId} className="c-va-card c-va-gap">
            <div className="c-va-gap-head">
              <span className="c-va-gap-question">{headline}</span>
              <Tag tone={one.callerCount > 30 ? "accent-2" : "neutral"}>
                {String(one.callerCount)} {one.callerCount === 1 ? portalText("operations", "caller_14dc69de") : portalText("operations", "callers_5bbff698")}
              </Tag>
              <span className="c-va-gap-when">{portalText("operations", "last_c7020181")}{one.lastSeenText}</span>
            </div>

            {quote === null ? null : <p className="c-va-gap-quote">{quote}</p>}

            {one.state === "queued" ? (
              <>
                <p className="c-va-gap-queued">
                  <Icon glyph={GLYPH.checkCircle} size="lg" />
                  {portalText("operations", "queued_for_the_next_version_the_agent_answers_it_once_you_7ed59b62")}</p>
                <p className="c-va-gap-quote">{one.answerText}</p>
              </>
            ) : one.state === "folded" && one.answeredIntoLabel !== null && draftLabel.has(one.answeredIntoLabel) ? (
              <>
                <p className="c-va-gap-queued">
                  <Icon glyph={GLYPH.gitBranch} size="lg" />
                  {portalText("operations", "in_a_draft_eff81cc7")}{one.answeredIntoLabel}{portalText("operations", "the_agent_answers_it_once_98eae339")}{one.answeredIntoLabel} {portalText("operations", "is_published_from_versions_46bfbb67")}</p>
                <p className="c-va-gap-quote">{one.answerText}</p>
              </>
            ) : answering === one.gapId ? (
              <div className="c-va-gap-answer">
                <Field label={portalText("operations", "your_answer_3ae5ff6e")}>
                  {(control) => (
                    <Textarea
                      {...control}
                      rows={3}
                      placeholder={portalText("operations", "answer_it_the_way_you_would_on_the_phone_b620496d")}
                      value={draft}
                      onChange={(event) => {
                        setDraft(event.currentTarget.value);
                      }}
                    />
                  )}
                </Field>
                <div className="c-va-gap-actions">
                  <Button
                    variant="primary"
                    disabled={draft.trim() === "" || answer.isPending}
                    onClick={() => {
                      answer.mutate({ gapId: one.gapId, text: draft.trim() });
                    }}
                  >
                    {portalText("operations", "add_to_the_next_version_bc39dd9d")}</Button>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setAnswering(null);
                      setDraft("");
                    }}
                  >
                    {portalText("operations", "cancel_77dfd213")}</Button>
                </div>
              </div>
            ) : (
              <div className="c-va-gap-actions">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setAnswering(one.gapId);
                    setDraft(one.answerText ?? "");
                  }}
                >
                  {portalText("operations", "write_the_answer_12efbfc5")}</Button>
                <Button
                  variant="ghost"
                  disabled={one.lastConversationId === null}
                  title={
                    one.lastConversationId === null
                      ? portalText("operations", "no_recorded_call_asked_this_question_3e762436")
                      : portalText("operations", "opens_the_latest_call_that_asked_it_in_history_4f2571bf")
                  }
                  onClick={() => {
                    if (one.lastConversationId !== null) onOpenConversation(one.lastConversationId);
                  }}
                >
                  {portalText("operations", "open_the_call_64ebe3b3")}</Button>
                <span className="c-va-gap-actions-end">
                  <Button
                    variant="ghost"
                    disabled={dismiss.isPending}
                    onClick={() => {
                      dismiss.mutate(one.gapId);
                    }}
                  >
                    {portalText("operations", "not_worth_answering_ec81effd")}</Button>
                </span>
              </div>
            )}
          </div>
          );
        })}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------------------
// Flagged
// ---------------------------------------------------------------------------------------

/** `raised_by_kind`, in the customer's words. Four populations, four sentences. */
function raisedByLabel(kind: ConversationFlagView["raisedByKind"]): string {
  switch (kind) {
    case "member":
      return portalText("operations", "raised_by_someone_on_your_team_4e4248a8");
    case "operator":
      return portalText("operations", "raised_by_wetalk_1994f592");
    case "carrier":
      return portalText("operations", "reported_by_the_carrier_90c8ce88");
    default:
      return portalText("operations", "flagged_automatically_c4422fab");
  }
}

/** The reason code, which is enumerated rather than free prose, as a sentence. */
function reasonLabel(reasonCode: string): string {
  switch (reasonCode) {
    case "asked_for_a_person":
      return portalText("operations", "the_caller_asked_for_a_person_65af78ac");
    case "caller_repeated_themselves":
      return portalText("operations", "the_caller_repeated_themselves_4cedb4b0");
    case "hung_up_early":
      return portalText("operations", "the_caller_hung_up_early_58f30481");
    case "price_disputed":
      return portalText("operations", "the_caller_disputed_a_price_a39e96f0");
    case "language_unavailable":
      return portalText("operations", "the_caller_s_language_was_not_available_3e5b9ced");
    default:
      // An unknown code is shown as itself rather than as a guess: a new reason the runtime
      // starts emitting should read oddly rather than be silently mislabelled.
      return reasonCode;
  }
}

function ConversationFlagPane({
  voiceAgentId,
  row,
  onOpenConversation,
}: {
  readonly voiceAgentId: string;
  readonly row: readonly ConversationFlagView[] | undefined;
  readonly onOpenConversation: (conversationId: string) => void;
}): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();

  const resolve = useMutation({
    mutationFn: (conversationFlagId: string) =>
      gateway.resolveConversationFlag(voiceAgentId, conversationFlagId),
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.conversationFlag });
    },
  });

  if (row === undefined) return <p className="c-note">{portalText("operations", "reading_the_flagged_conversations_f83594f8")}</p>;
  if (row.length === 0) {
    return <p className="c-note">{portalText("operations", "nothing_is_waiting_for_a_human_ear_ef57404f")}</p>;
  }

  return (
    <>
      <p className="c-va-dek">
        {portalText("operations", "conversations_worth_a_human_ear_the_caller_repeated_themse_792b8a06")}</p>

      <div className="c-va-gaps">
        {row.map((one) => (
          <div key={one.conversationFlagId} className="c-va-card c-va-flag">
            <span className="c-va-flag-mark">
              <Icon
                glyph={one.severity === "urgent" ? GLYPH.warningCircle : GLYPH.question}
                size="lg"
              />
            </span>
            <span className="c-va-flag-body">
              <span className="c-va-flag-head">
                <span className="c-va-flag-reason">{reasonLabel(one.reasonCode)}</span>
                {one.severity === "urgent" ? <Tag status="critical">{portalText("operations", "urgent_8d0cdea0")}</Tag> : null}
                <span className="c-va-flag-meta">
                  {raisedByLabel(one.raisedByKind)} · {one.raisedAtText}
                  {one.billedSeconds === null
                    ? ""
                    : ` · ${formatDuration(one.billedSeconds)}`}
                  {one.partyAddress === null ? "" : ` · ${one.partyAddress}`}
                </span>
              </span>
              {one.excerpt === null ? null : (
                <span className="c-va-flag-quote">{one.excerpt}</span>
              )}
            </span>
            <span className="c-va-flag-actions">
              <Button
                variant="secondary"
                onClick={() => {
                  onOpenConversation(one.conversationId);
                }}
              >
                {portalText("operations", "listen_c518fb06")}</Button>
              <Button
                variant="ghost"
                disabled={resolve.isPending}
                onClick={() => {
                  resolve.mutate(one.conversationFlagId);
                }}
              >
                {portalText("operations", "resolve_ac7f958c")}</Button>
            </span>
          </div>
        ))}
      </div>
    </>
  );
}
